/**
 * El cliente HTTP del dashboard. **Todo** el tráfico a la API pasa por acá.
 *
 * Qué resuelve, y por qué está centralizado
 * -----------------------------------------
 * 1. **Bearer + refresh automático.** El access token dura una hora; el
 *    refresh es una cadena opaca que la API **rota en cada uso** y cuya
 *    reutilización cierra todas las sesiones del usuario. Eso convierte al
 *    refresh en un recurso de un solo uso, y de ahí sale la regla que hace
 *    que este archivo exista:
 *
 *      **nunca puede haber dos refresh en vuelo con el mismo token.**
 *
 *    Si el dashboard abre seis consultas al montar y las seis reciben 401,
 *    seis refresh concurrentes usan el mismo refresh token; la API ve cinco
 *    reutilizaciones y desloguea al usuario. Se serializa con un
 *    `navigator.locks` (que además cubre varias pestañas del mismo origen) y,
 *    donde no exista, con una promesa compartida en el proceso.
 *
 * 2. **Refresco proactivo.** Si al token le quedan menos de 60 s, se refresca
 *    *antes* de mandar la request. Ahorra el 401 y evita que una mutación
 *    (por ejemplo, un recall) se ejecute dos veces por el reintento.
 *
 * 3. **Errores con forma única.** Ver `errors.ts`.
 *
 * 4. **Correlación.** Cada request lleva `X-Request-Id`; la respuesta trae
 *    `X-Trace-Id`, que queda dentro del `ApiError` y se muestra en pantalla.
 *
 * Lo que este cliente NO hace: no sabe de React, no sabe de rutas y no
 * navega. Cuando la sesión muere avisa por `onSessionEnded` y el
 * `AuthProvider` decide qué pantalla mostrar.
 */
import { apiErrorFromResponse, ApiError, networkError } from './errors';
import type { QueryParams } from './types';
import {
  sessionFromPair,
  type StoredSession,
  type TokenPairResponse,
  type TokenStore,
} from '@/auth/tokens';

/** Margen antes del vencimiento a partir del cual se refresca sin esperar el 401. */
const REFRESH_SKEW_MS = 60_000;

/** Corta una request colgada. La API mata sus consultas a los 5 s. */
const DEFAULT_TIMEOUT_MS = 20_000;

const REFRESH_LOCK = 'xyvona.dashboard.refresh';

/** Por qué se terminó la sesión. La UI dice cosas distintas en cada caso. */
export type SessionEndReason =
  | 'logout' // el usuario cerró sesión
  | 'expired' // el refresh venció o ya no es válido
  | 'reused' // la API detectó reutilización: se cerró todo por seguridad
  | 'revoked'; // un admin o el propio usuario cerró las sesiones

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** Cuerpo JSON. Se serializa acá. */
  body?: unknown;
  query?: QueryParams;
  signal?: AbortSignal;
  /** `false` para endpoints públicos (`/auth/login`). Por defecto `true`. */
  auth?: boolean;
  timeoutMs?: number;
}

export interface ApiClientOptions {
  /** Prefijo. Vacío = mismo origen (dev: proxy de Vite; prod: CloudFront). */
  baseUrl?: string;
  tokens: TokenStore;
  onSessionEnded?: (reason: SessionEndReason, error: ApiError | null) => void;
  fetchImpl?: typeof fetch;
  now?: () => number;
  /** Sólo tests: saltea `navigator.locks`. */
  useWebLocks?: boolean;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly tokens: TokenStore;
  private readonly onSessionEnded: (reason: SessionEndReason, error: ApiError | null) => void;
  private readonly fetchImpl: typeof fetch;
  private readonly now: () => number;
  private readonly useWebLocks: boolean;

  /** Refresh en vuelo, compartido por todas las requests de esta pestaña. */
  private inFlightRefresh: Promise<string> | null = null;

  constructor(options: ApiClientOptions) {
    this.baseUrl = (options.baseUrl ?? '').replace(/\/$/, '');
    this.tokens = options.tokens;
    this.onSessionEnded = options.onSessionEnded ?? (() => undefined);
    // Ligado a `globalThis`: `fetch` desligado tira `Illegal invocation`.
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.now = options.now ?? Date.now;
    this.useWebLocks = options.useWebLocks ?? true;
  }

  // ───────────────────────────────────────────────────────────────── verbos

  get<T>(path: string, query?: QueryParams, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'GET', ...(query ? { query } : {}) });
  }

  post<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'POST', body });
  }

  patch<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PATCH', body });
  }

  delete<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'DELETE' });
  }

  // ───────────────────────────────────────────────────────────── el request

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const needsAuth = options.auth !== false;
    const token = needsAuth ? await this.accessTokenForRequest() : null;

    const first = await this.send(path, options, token);

    // Un 401 con un token que parecía vigente significa que la API lo
    // rechazó igual: venció entre la comprobación y la llegada, o se revocó.
    // Un solo reintento — dos serían un bucle si el refresh devuelve tokens
    // que tampoco sirven. Un 403 (`insufficient_role`) NO se reintenta: el
    // rol no cambia por refrescar el token.
    if (first.status === 401 && needsAuth) {
      const cause = await apiErrorFromResponse(first.clone());
      const fresh = await this.refreshAccessToken(token, cause);
      const second = await this.send(path, options, fresh);
      return this.parse<T>(second);
    }

    return this.parse<T>(first);
  }

  // ───────────────────────────────────────────────────────────────── la URL

  /**
   * Path de la API → URL que el navegador tiene que pedir, con el prefijo del
   * ambiente adelante.
   *
   * No es un verbo más: los verbos hablan JSON, y hay respuestas que no lo son
   * —el SVG del QR, el paquete de evidencia, la exportación— y que por eso se
   * traen con `fetch` a mano. Lo que esas llamadas no pueden hacer es armar la
   * URL por su cuenta: `baseUrl` es `/capi` en el ambiente desplegado, y un
   * path absoluto sin ese prefijo cae en **verify**, que no conoce las rutas
   * del panel y contesta 404. Pasó con el QR de la ficha (#140).
   */
  resolve(path: string, query?: QueryParams): string {
    return this.baseUrl + path + encodeQuery(query);
  }

  /**
   * El `fetch` que usa el cliente. Lo necesitan las mismas llamadas que
   * `resolve()`: si traen el archivo con `globalThis.fetch` no heredan el
   * transporte de la aplicación, y en los tests eso significa salir a la red de
   * verdad en vez de pegarle al doble — que es por qué el prefijo faltante
   * sobrevivió a la suite.
   */
  get fetcher(): typeof fetch {
    return this.fetchImpl;
  }

  // ─────────────────────────────────────────────────────────────── sesión

  /** Sesión persistida, o `null`. Lectura sin efectos. */
  currentSession(): StoredSession | null {
    return this.tokens.read();
  }

  /**
   * Access token utilizable. Refresca si está por vencer.
   * `ApiError('no_session')` si no hay nada que usar — la vista lo trata
   * como "andá al login", no como un fallo de red.
   */
  private async accessTokenForRequest(): Promise<string> {
    const session = this.tokens.read();
    if (session === null) {
      throw new ApiError({ status: 401, code: 'no_session', message: 'No hay sesión iniciada.' });
    }
    if (session.accessExpiresAt - this.now() > REFRESH_SKEW_MS) {
      return session.accessToken;
    }
    return this.refreshAccessToken(session.accessToken, null);
  }

  /**
   * Rota el par de tokens. Serializado: ver el punto 1 del encabezado.
   *
   * `staleToken` es el access que resultó insuficiente. Sirve para detectar
   * que otra request (u otra pestaña) ya rotó mientras esperábamos el turno,
   * en cuyo caso no se rota de nuevo.
   */
  private async refreshAccessToken(staleToken: string | null, cause: ApiError | null): Promise<string> {
    this.inFlightRefresh ??= this.withLock(() => this.doRefresh(staleToken, cause)).finally(() => {
      this.inFlightRefresh = null;
    });
    return this.inFlightRefresh;
  }

  private async withLock<T>(fn: () => Promise<T>): Promise<T> {
    const locks: LockManager | undefined = this.useWebLocks ? navigator.locks : undefined;
    if (locks === undefined) return fn();
    return locks.request(REFRESH_LOCK, fn);
  }

  private async doRefresh(staleToken: string | null, cause: ApiError | null): Promise<string> {
    const session = this.tokens.read();
    if (session === null) {
      const error = new ApiError({
        status: 401,
        code: 'no_session',
        message: 'La sesión se cerró en otra pestaña.',
      });
      this.endSession('expired', error);
      throw error;
    }

    // Otro la rotó mientras esperábamos: el token nuevo sirve, no se toca.
    if (session.accessToken !== staleToken && session.accessExpiresAt - this.now() > REFRESH_SKEW_MS) {
      return session.accessToken;
    }

    if (session.refreshExpiresAt <= this.now()) {
      const error =
        cause ??
        new ApiError({
          status: 401,
          code: 'invalid_refresh_token',
          message: 'La sesión venció. Iniciá sesión de nuevo.',
        });
      this.endSession('expired', error);
      throw error;
    }

    const response = await this.send(
      '/api/v1/auth/refresh',
      { method: 'POST', body: { refresh_token: session.refreshToken }, auth: false },
      null,
    );

    if (!response.ok) {
      const error = await apiErrorFromResponse(response);
      // `refresh_token_reused` no es "se venció": la API acaba de cerrar
      // TODAS las sesiones del usuario porque vio el mismo token dos veces.
      // Merece su propio mensaje — si no, el usuario cree que el sistema
      // falla solo y nadie investiga una posible filtración.
      this.endSession(error.code === 'refresh_token_reused' ? 'reused' : 'expired', error);
      throw error;
    }

    const pair = await this.parse<TokenPairResponse>(response);
    const next = sessionFromPair(pair, this.now());
    // Se persiste ANTES de resolver: si el proceso muere entre la respuesta y
    // el guardado, el refresh viejo ya no sirve y el usuario queda afuera sin
    // motivo aparente.
    this.tokens.write(next);
    return next.accessToken;
  }

  /** Borra la sesión y avisa. Idempotente. */
  endSession(reason: SessionEndReason, error: ApiError | null = null): void {
    this.tokens.clear();
    this.inFlightRefresh = null;
    this.onSessionEnded(reason, error);
  }

  // ─────────────────────────────────────────────────────────────── plomería

  private async send(
    path: string,
    options: RequestOptions,
    token: string | null,
  ): Promise<Response> {
    const headers = new Headers({ Accept: 'application/json' });
    if (token !== null) headers.set('Authorization', `Bearer ${token}`);
    // Se manda siempre: la API lo adopta como `trace_id` y así el id que ve
    // el usuario en pantalla es el mismo que está en el log del servidor.
    headers.set('X-Request-Id', newRequestId());

    let payload: string | undefined;
    if (options.body !== undefined && options.method !== 'GET') {
      headers.set('Content-Type', 'application/json');
      payload = JSON.stringify(options.body);
    }

    const timeout = AbortSignal.timeout(options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
    const signal =
      options.signal === undefined ? timeout : AbortSignal.any([options.signal, timeout]);

    try {
      return await this.fetchImpl(this.baseUrl + path + encodeQuery(options.query), {
        method: options.method ?? 'GET',
        headers,
        ...(payload === undefined ? {} : { body: payload }),
        signal,
        // La API no usa cookies; mandarlas sería superficie de CSRF gratis.
        credentials: 'omit',
        redirect: 'error',
      });
    } catch (cause) {
      throw networkError(cause);
    }
  }

  private async parse<T>(response: Response): Promise<T> {
    if (!response.ok) throw await apiErrorFromResponse(response);
    // 204: `logout` y los `DELETE`. `undefined as T` es deliberado — el
    // llamador declara `Promise<void>` y nadie lee el valor.
    if (response.status === 204 || response.headers.get('Content-Length') === '0') {
      return undefined as T;
    }
    try {
      return (await response.json()) as T;
    } catch (cause) {
      throw new ApiError({
        status: response.status,
        code: 'malformed_response',
        message: 'La API devolvió una respuesta que no se pudo leer.',
        traceId: response.headers.get('X-Trace-Id'),
        cause,
      });
    }
  }
}

/** `?a=1&b=2`, o cadena vacía. `undefined` y `null` se omiten. */
export function encodeQuery(query: QueryParams | undefined): string {
  if (query === undefined) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    params.set(key, String(value));
  }
  const encoded = params.toString();
  return encoded.length > 0 ? `?${encoded}` : '';
}

function newRequestId(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}
